import { FC, ReactNode } from 'react';
import getClassNames from 'src/common/utils/getClassNames';
import { withSuffix } from 'src/common/utils/dataAttributeHelpers';
import classNames from 'classnames';
import styles from './Label.module.scss';

export type Variants = 'columns' | 'inline' | 'row' | 'row-reverse' | 'row-space-between';

const sizes = [
  's',
  'm',
  'l',
] as const;

export type Size = typeof sizes[number];

export type LabelProps = {
  label: string;
  /** Allows targeting a specific form element that is not a child */
  htmlFor?: string;
  required?: boolean;
  /** Slot for including an element directly after the `label` and `required` text */
  inlineSlot?: ReactNode;
  /** Slot for including an element to the far right of the label */
  rightSlot?: ReactNode;
  /** For wrapping an `<input />` element */
  children?: ReactNode;
  /** For modifying styling slightly */
  variant?: Variants;
  /** For setting font size */
  size?: Size;
  /** Don't include padding between label and control */
  compact?: boolean;
};

const Label: FC<LabelProps> = ({
  children,
  variant = 'columns',
  size = 'l',
  label,
  htmlFor = undefined,
  required = false,
  inlineSlot = null,
  rightSlot = null,
  compact = false,
}) => (
  <label
    className={getClassNames(styles, 'root', variant)}
    htmlFor={children == null ? htmlFor : undefined}
  >
    <div
      className={getClassNames(styles, 'row', classNames(variant, { compact }))}
      data-test={withSuffix(label, 'label')}
    >
      <span className={getClassNames(styles, 'label', classNames(variant, `body-${size}`))}>
        {label}
      </span>
      {required && <span className={getClassNames(styles, 'required', variant)}>*</span>}
      {' '}
      {inlineSlot && (
        <span className={getClassNames(styles, 'inlineSlot', variant)}>
          {inlineSlot}
        </span>
      )}
      {' '}
      {rightSlot && (
        <span className={getClassNames(styles, 'rightSlot', variant)}>
          {rightSlot}
        </span>
      )}
    </div>
    {children}
  </label>
);

export default Label;
